/**
 * Flowlytics Pixel SDK - Session Tracking
 * Manages user sessions with localStorage (Read-Only cookies)
 */

class SessionCollector {
  constructor(config) {
    this.config = config;
    this.storageKey = "flowlytics_session";
    this.sessionTimeout = config.sessionTimeout;
  }

  /**
   * Get or create session
   */
  getOrCreateSession() {
    const session = this.getSession();

    if (session && !this.isExpired(session)) {
      // Update last activity
      this.updateSession(session.id);
      return session.id;
    }

    // Create new session
    return this.createSession();
  }

  /**
   * Create new session.
   * Session object is held in memory only — call persistIfConsented() after
   * analytics consent is granted. This prevents writing to localStorage
   * before the user has made a consent decision (ePrivacy Art. 5(3)).
   */
  createSession() {
    const sessionId = this.generateSessionId();
    const session = {
      id: sessionId,
      created_at: Date.now(),
      last_activity: Date.now(),
      page_views: 1,
      landing_page: window.location.href,
      referrer: document.referrer,
      utm_source: this.getUTMParam("utm_source"),
      utm_medium: this.getUTMParam("utm_medium"),
      utm_campaign: this.getUTMParam("utm_campaign"),
      utm_term: this.getUTMParam("utm_term"),
      utm_content: this.getUTMParam("utm_content"),
    };

    // Hold in memory — do not write to localStorage until analytics consent granted
    this._pendingSession = session;

    if (this.config.debug) {
      console.log(
        "[Flowlytics Session] New session created (in-memory):",
        sessionId,
      );
    }

    return sessionId;
  }

  /**
   * Persist pending session to localStorage after analytics consent is granted.
   * Safe to call multiple times — no-op if already persisted.
   */
  persistIfConsented(analyticsGranted) {
    if (analyticsGranted && this._pendingSession) {
      this.saveSession(this._pendingSession);
      this._pendingSession = null;
    }
  }

  /**
   * Get current session from storage
   */
  getSession() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Save session to storage
   */
  saveSession(session) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(session));
    } catch (e) {
      console.error("[Flowlytics Session] Failed to save session:", e);
    }
  }

  /**
   * Update session last activity
   */
  updateSession(sessionId) {
    const session = this.getSession();
    if (session && session.id === sessionId) {
      session.last_activity = Date.now();
      session.page_views = (session.page_views || 0) + 1;
      this.saveSession(session);
    }
  }

  /**
   * Check if session is expired
   */
  isExpired(session) {
    const now = Date.now();
    const elapsed = now - session.last_activity;
    return elapsed > this.sessionTimeout;
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get UTM parameter from URL
   */
  getUTMParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param) || null;
  }

  /**
   * Get all UTM parameters
   */
  getUTMParams() {
    return {
      utm_source: this.getUTMParam("utm_source"),
      utm_medium: this.getUTMParam("utm_medium"),
      utm_campaign: this.getUTMParam("utm_campaign"),
      utm_term: this.getUTMParam("utm_term"),
      utm_content: this.getUTMParam("utm_content"),
    };
  }

  /**
   * Collect current session data
   */
  collect() {
    const session = this.getSession();

    if (!session) {
      return {
        session_id: null,
        session_status: "no_session",
      };
    }

    return {
      session_id: session.id,
      session_created_at: new Date(session.created_at).toISOString(),
      session_duration: Date.now() - session.created_at,
      session_page_views: session.page_views,
      session_landing_page: session.landing_page,
      session_referrer: session.referrer,
      utm_params: {
        utm_source: session.utm_source,
        utm_medium: session.utm_medium,
        utm_campaign: session.utm_campaign,
        utm_term: session.utm_term,
        utm_content: session.utm_content,
      },
    };
  }

  /**
   * End current session
   */
  endSession() {
    try {
      localStorage.removeItem(this.storageKey);
      if (this.config.debug) {
        console.log("[Flowlytics Session] Session ended");
      }
    } catch (e) {
      console.error("[Flowlytics Session] Failed to end session:", e);
    }
  }
}

export default SessionCollector;
